import {HoistModel, LoadSpec, XH, managed, persist} from '@xh/hoist/core';
import {action, bindable, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {Portfolio, RiskMetrics} from '../../../../data/DataTypes';
import {ChartModel} from '@xh/hoist/cmp/chart';
import {asServerException} from '../../../../../core/ServiceUtils';
import {DepositsDialogModel} from './DepositsDialogModel';
import {View} from '@xh/hoist/data';
import {marketValueField, pnlDlyField, pnlYtdField, tickerField} from '../../../../cmp/PositionColumns';
import {first, last, orderBy} from 'lodash';
import {Timer} from '@xh/hoist/utils/async';
import {MINUTES} from '@xh/hoist/utils/datetime';
import {areaGradient, CHART_DOWN_COLOR, CHART_UP_COLOR} from '../../../../cmp/ChartColors';

export type PortfolioTotals = {
    totalBalance: number;
    marketValue: number;
    cashBalance: number;
    marginBalance: number;
    pnlDly: number;
    pnlDlyPct: number;
    pnlYtd: number;
};

/** Balance change over the selected chart range. */
export type RangeChange = {
    amount: number;
    pct: number;
};

export class PortfolioModel extends HoistModel {
    override persistWith = {prefKey: 'portfoliosTabState'};
    @bindable @persist accessor portfolioId: number;
    @observableRef accessor totals: PortfolioTotals = <PortfolioTotals>{};
    @observableRef accessor rangeChange: RangeChange = null;
    @observable accessor showInactivePortfolios = false;
    @bindable @persist accessor range: string = '';
    @managed accountChart = new ChartModel({highchartsConfig: this.createChartModel()});
    @managed depositsDialog = new DepositsDialogModel();
    /** Today's balance series is recorded by the backend and has no live bar, so it is polled. */
    @managed private intradayTimer = Timer.create({
        interval: MINUTES,
        runFn: () => XH.intradayService.loadPortfolioAsync(this.portfolioId, true)
    });
    private dataView: View;

    constructor() {
        super();

        this.dataView = XH.livePositionsService.cube.createView({
            query: {
                includeLeaves: true,
                dimensions: ['ticker'],
                filter: {field: 'portfolioId', op: '=', value: -1}
            },
            connect: true
        });

        this.addReaction({
            track: () => this.dataView.lastUpdated,
            run: () => this.updateTotals()
        });

        this.addReaction({
            track: () => this.portfolioId,
            run: () => this.loadAsync()
        });

        this.addReaction({
            track: () => this.range,
            run: range => {
                this.updateChart();
            }
        });
    }

    get portfolioOptions(): Portfolio[] {
        const portfolios = orderBy(
            XH.portfolioService.portfolios.filter(p => p.isActive || this.showInactivePortfolios),
            ['isActive', 'portfolioName'],
            ['desc', 'asc']
        );

        return portfolios;
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (!this.portfolioId) return;

        this.dataView.updateQuery({
            filter: {field: 'portfolioId', op: '=', value: this.portfolioId}
        });

        this.updateTotals();

        this.updateChart();

        XH.intradayService.loadPortfolioAsync(this.portfolioId, true);
    }

    /** Beta and Sharpe of the selected portfolio, calculated at EOD close. Null for a cash portfolio. */
    get risk(): RiskMetrics {
        return XH.performanceService.calcData?.portfolioRisk?.find(r => r.portfolioId === this.portfolioId) ?? null;
    }

    showDeposits() {
        this.depositsDialog.open(this.portfolioId);
    }

    @action
    toggleShowInactivePortfolios() {
        this.showInactivePortfolios = !this.showInactivePortfolios;
    }

    private updateTotals() {
        const totals: PortfolioTotals = {
            marketValue: 0,
            cashBalance: 0,
            marginBalance: 0,
            pnlDly: 0,
            pnlYtd: 0,
            pnlDlyPct: 0,
            totalBalance: 0
        };
        const rows = this.dataView.result.rows;
        rows.forEach(row => {
            if (first(row[tickerField.name]) === '$') totals.cashBalance += row[marketValueField.name];
            else totals.marketValue += row[marketValueField.name];
            totals.pnlDly += row[pnlDlyField.name];
            totals.pnlYtd += row[pnlYtdField.name];
        });
        totals.totalBalance = totals.marketValue + totals.cashBalance;
        totals.pnlDlyPct = totals.marketValue == 0 ? 0 : totals.pnlDly / (totals.marketValue - totals.pnlDly);

        runInAction(() => {
            this.totals = totals;
        });
    }

    private async updateChart() {
        try {
            const portfolioHistory = await XH.portfolioService.fetchPortfolioHistoryAsync(this.portfolioId, this.range);

            const points: [number, number][] = portfolioHistory.map(it => [Date.parse(it.asOfDate), it['totalBalance']]),
                startBalance = first(points)?.[1],
                endBalance = last(points)?.[1],
                isUp = !(endBalance < startBalance),
                color = isUp ? CHART_UP_COLOR : CHART_DOWN_COLOR;

            const mvSeries = {
                name: 'Balance',
                type: 'area',
                color,
                lineWidth: 1.5,
                threshold: null,
                fillColor: areaGradient(color),
                // Mark the latest balance with a dot at the end of the line.
                data: points.map(([x, y], idx) => (idx === points.length - 1 ? {x, y, marker: {enabled: true, radius: 3}} : [x, y]))
            };

            runInAction(() => {
                this.rangeChange =
                    startBalance && endBalance != null ? {amount: endBalance - startBalance, pct: (endBalance - startBalance) / startBalance} : null;
                // Dashed reference line at the balance the range started from.
                this.accountChart.updateHighchartsConfig({
                    yAxis: {
                        plotLines:
                            startBalance != null ? [{value: startBalance, color: 'rgba(128, 128, 128, 0.6)', dashStyle: 'ShortDash', width: 1, zIndex: 3}] : []
                    }
                });
                this.accountChart.setSeries(mvSeries);
            });
        } catch (e) {
            XH.handleException(asServerException(e), {
                title: 'Error loading account history',
                logOnServer: false
            });
        }
    }

    private createChartModel() {
        return {
            chart: {zoomType: 'x', margin: 0, animation: false},
            navigator: {enabled: false},
            rangeSelector: {enabled: false},
            exporting: {enabled: false},
            legend: {enabled: false},
            scrollbar: {enabled: false},
            xAxis: {type: 'datetime', labels: {enabled: false}, tickPosition: 'inside'},
            yAxis: {labels: {enabled: false}, title: {text: null}, tickPosition: 'inside'},
            plotOptions: {
                series: {animation: false},
                area: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                }
            },
            tooltip: {
                valueDecimals: 2,
                valuePrefix: '$',
                valueSuffix: ' USD',
                xDateFormat: '%Y-%m-%d'
            }
        };
    }
}
